// File: frontend/src/pages/IOCSearchPage.tsx
import React, { useState } from 'react';
import { useApi } from '../hooks/useApi';
import {
    Box, 
    Button, 
    TextField, 
    Typography, 
    Paper, 
    CircularProgress, 
    Alert,
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow, 
    Link,
    Container,
    styled,
    alpha,
    Fade,
    Chip,
    Stack,
    InputAdornment,
    FormControlLabel,
    Switch,
    Select,
    MenuItem,
    FormControl,
    InputLabel
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { IOCSearchResult } from '../types';
import { cyber } from '../theme';
import SearchIcon from '@mui/icons-material/Search';
import SecurityIcon from '@mui/icons-material/Security';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import ArticleIcon from '@mui/icons-material/Article';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import VisibilityIcon from '@mui/icons-material/Visibility';
import BugReportIcon from '@mui/icons-material/BugReport';
import DownloadIcon from '@mui/icons-material/Download';
import BulkTriage from '../components/BulkTriage';
import KGIndicatorMatches from '../components/KGIndicatorMatches';

// Styled Components
const HeroSection = styled(Box)(({ theme }) => ({
  background: cyber.gradient,
  border: `1px solid ${alpha(cyber.primaryLight, 0.35)}`,
  borderRadius: theme.spacing(2),
  padding: theme.spacing(4),
  marginBottom: theme.spacing(4),
  color: 'white',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-50%',
    right: '-10%',
    width: '300px',
    height: '300px',
    background: 'rgba(255, 255, 255, 0.08)',
    borderRadius: '50%',
    filter: 'blur(60px)',
  }
}));

const SearchCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  background: alpha(theme.palette.background.paper, 0.9),
  backdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(2),
  border: `1px solid ${alpha(cyber.primary, 0.2)}`,
  marginBottom: theme.spacing(4),
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  '& .MuiTableHead-root': {
    background: alpha(theme.palette.primary.main, 0.05),
  },
  '& .MuiTableRow-root': {
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      background: alpha(theme.palette.primary.main, 0.03),
    }
  }
}));

const TypeChip = styled(Chip)(({ theme }) => ({
  fontWeight: 'bold',
  fontSize: '0.75rem',
}));

const SearchButton = styled(Button)(({ theme }) => ({
  background: cyber.gradient,
  color: 'white',
  fontWeight: 'bold',
  height: '56px',
  padding: '0 32px',
  borderRadius: theme.spacing(1),
  textTransform: 'none',
  fontSize: '1rem',
  boxShadow: `0 4px 12px ${alpha(cyber.primary, 0.3)}`,
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    background: cyber.gradientHover,
    boxShadow: cyber.glow,
    transform: 'translateY(-2px)',
  },
  '&:disabled': {
    background: theme.palette.action.disabledBackground,
    color: theme.palette.action.disabled,
  }
}));

const ExampleChip = styled(Chip)(({ theme }) => ({
  cursor: 'pointer',
  fontFamily: cyber.fontMono,
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: cyber.glow,
  }
}));

const getTypeColor = (type: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    const typeMap: { [key: string]: "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" } = {
        'ipv4-addr': 'primary',
        'ipv6-addr': 'primary',
        'domain-name': 'secondary',
        'url': 'info',
        'file': 'warning',
        'email-addr': 'error',
    };
    return typeMap[type.toLowerCase()] || 'default';
};

const IOCSearchPage = () => {
    const { authenticatedFetcher, authenticatedBlobFetcher } = useApi();
    const [searchTerm, setSearchTerm] = useState('');
    const [results, setResults] = useState<IOCSearchResult[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [searched, setSearched] = useState(false);
    const [submittedTerm, setSubmittedTerm] = useState('');

    // CSV export controls
    const [exportDays, setExportDays] = useState('30');
    const [exportDefang, setExportDefang] = useState(false);
    const [exportUnique, setExportUnique] = useState(true);
    const [isExporting, setIsExporting] = useState(false);
    const [exportError, setExportError] = useState<string | null>(null);

    const handleExport = async () => {
        setIsExporting(true);
        setExportError(null);
        try {
            const params = new URLSearchParams({
                format: 'csv',
                defang: String(exportDefang),
                unique: String(exportUnique),
            });
            if (exportDays !== 'all') {
                const since = new Date();
                since.setDate(since.getDate() - parseInt(exportDays, 10));
                params.set('since', since.toISOString());
            }
            const blob = await authenticatedBlobFetcher(`/api/iocs/export?${params.toString()}`);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `ti-mindmap-iocs-${new Date().toISOString().slice(0, 10)}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (err: any) {
            setExportError(err.message || 'Export failed.');
        } finally {
            setIsExporting(false);
        }
    };

    const handleSearch = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!searchTerm.trim()) return;

        setIsLoading(true);
        setError(null);
        setSearched(true);
        setSubmittedTerm(searchTerm.trim());
        setResults([]);

        try {
            const data = await authenticatedFetcher(`/api/iocs/search?q=${encodeURIComponent(searchTerm.trim())}`);
            setResults(data.results);
        } catch (err: any) {
            setError(err.message || 'Failed to fetch search results.');
        } finally {
            setIsLoading(false);
        }
    };

    const exampleIOCs = [
        { label: '192.168.1.1', icon: '🌐' },
        { label: 'malicious.com', icon: '🔗' },
        { label: 'abc123def456...', icon: '📝' },
    ];

    const exportPanel = (
        <Fade in timeout={800}>
            <Paper
                sx={{
                    p: 2.5,
                    mb: 4,
                    borderRadius: 2,
                    border: `1px solid ${alpha(cyber.accent.green, 0.25)}`,
                    background: alpha(cyber.accent.green, 0.03),
                }}
            >
                <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    alignItems={{ xs: 'flex-start', md: 'center' }}
                    justifyContent="space-between"
                >
                    <Box>
                        <Typography variant="subtitle1" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <DownloadIcon sx={{ color: cyber.accent.green }} />
                            Export IOCs to CSV
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Flat CSV for SIEM/EDR ingestion, with provenance and optional defanging.
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                        <FormControl size="small" sx={{ minWidth: 130 }}>
                            <InputLabel id="export-period-label">Period</InputLabel>
                            <Select
                                labelId="export-period-label"
                                value={exportDays}
                                label="Period"
                                onChange={(e) => setExportDays(e.target.value)}
                            >
                                <MenuItem value="7">Last 7 days</MenuItem>
                                <MenuItem value="30">Last 30 days</MenuItem>
                                <MenuItem value="90">Last 90 days</MenuItem>
                                <MenuItem value="all">All time</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControlLabel
                            control={<Switch size="small" checked={exportUnique} onChange={(e) => setExportUnique(e.target.checked)} />}
                            label={<Typography variant="caption">Unique indicators</Typography>}
                        />
                        <FormControlLabel
                            control={<Switch size="small" checked={exportDefang} onChange={(e) => setExportDefang(e.target.checked)} />}
                            label={<Typography variant="caption">Defang</Typography>}
                        />
                        <Button
                            variant="contained"
                            color="success"
                            size="small"
                            onClick={handleExport}
                            disabled={isExporting}
                            startIcon={isExporting ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
                            sx={{ textTransform: 'none', fontWeight: 'bold' }}
                        >
                            {isExporting ? 'Exporting…' : 'Download CSV'}
                        </Button>
                    </Stack>
                </Stack>
                {exportError && (
                    <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                        {exportError}
                    </Alert>
                )}
            </Paper>
        </Fade>
    );

    return (
        <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 4 }}>
            <Container maxWidth="lg">
                {/* Hero Section */}
                <Fade in timeout={500}>
                    <HeroSection>
                        <Box sx={{ position: 'relative', zIndex: 1 }}>
                            <Button
                                component="a"
                                href="https://docs.ti-mindmap-hub.com/outputs/ioc-extraction"
                                target="_blank"
                                rel="noopener noreferrer"
                                size="small"
                                startIcon={<MenuBookIcon />}
                                endIcon={<OpenInNewIcon sx={{ fontSize: '0.75rem !important' }} />}
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    right: 0,
                                    bgcolor: 'rgba(255,255,255,0.95)',
                                    color: cyber.primary,
                                    borderRadius: '20px',
                                    fontWeight: 700,
                                    fontSize: '0.78rem',
                                    px: 2,
                                    textDecoration: 'none',
                                    '&:hover': {
                                        bgcolor: 'white',
                                        boxShadow: cyber.glow,
                                    },
                                }}
                            >
                                Documentation
                            </Button>
                            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                                <FingerprintIcon sx={{ fontSize: 48 }} />
                                <Typography variant="h3" component="h1" fontWeight="bold">
                                    IOC Search
                                </Typography>
                            </Stack>
                            <Typography variant="h6" sx={{ mb: 2, opacity: 0.95, maxWidth: '800px' }}>
                                Search for Indicators of Compromise across our entire threat intelligence database
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                <Chip
                                    icon={<SecurityIcon />}
                                    label="IP Addresses"
                                    sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', color: 'white', fontWeight: 'bold' }}
                                />
                                <Chip
                                    icon={<BugReportIcon />}
                                    label="Domains"
                                    sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', color: 'white', fontWeight: 'bold' }}
                                />
                                <Chip
                                    icon={<ArticleIcon />}
                                    label="File Hashes"
                                    sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', color: 'white', fontWeight: 'bold' }}
                                />
                            </Stack>
                        </Box>
                    </HeroSection>
                </Fade>

                {/* Search Card */}
                <Fade in timeout={700}>
                    <SearchCard elevation={3}>
                        <Box component="form" onSubmit={handleSearch} noValidate>
                            <Stack spacing={3}>
                                <Box>
                                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <SearchIcon color="primary" />
                                        Search for IOC
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                        Enter an IP address, domain, file hash, or any other indicator
                                    </Typography>
                                </Box>

                                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                                    <TextField
                                        fullWidth
                                        id="ioc"
                                        label="IOC Value"
                                        name="ioc"
                                        autoComplete="off"
                                        autoFocus
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        placeholder="e.g., 192.168.1.1, malicious.com, abc123def..."
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <FingerprintIcon color="action" />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 2,
                                            }
                                        }}
                                    />
                                    <SearchButton
                                        type="submit"
                                        disabled={isLoading || !searchTerm.trim()}
                                        startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                                    >
                                        {isLoading ? 'Searching...' : 'Search'}
                                    </SearchButton>
                                </Stack>

                                {/* Example IOCs */}
                                <Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                        Example searches:
                                    </Typography>
                                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                        {exampleIOCs.map((example, index) => (
                                            <ExampleChip
                                                key={index}
                                                label={`${example.icon} ${example.label}`}
                                                size="small"
                                                variant="outlined"
                                                onClick={() => setSearchTerm(example.label)}
                                            />
                                        ))}
                                    </Stack>
                                </Box>
                            </Stack>
                        </Box>
                    </SearchCard>
                </Fade>

                {/* Bulk IOC Triage */}
                <BulkTriage />

                {/* CSV Export */}
                {exportPanel}

                {/* Error Alert */}
                {error && (
                    <Fade in timeout={300}>
                        <Alert severity="error" sx={{ mb: 4, borderRadius: 2 }}>
                            {error}
                        </Alert>
                    </Fade>
                )}

                {/* Results Section */}
                {searched && !isLoading && !error && (
                    <Fade in timeout={900}>
                        <Box>
                            <Paper 
                                sx={{ 
                                    p: 3, 
                                    mb: 3, 
                                    borderRadius: 2,
                                    background: alpha(cyber.primary, 0.05),
                                    border: `1px solid ${alpha(cyber.primary, 0.2)}`
                                }}
                            >
                                <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                                    <Box>
                                        <Stack direction="row" spacing={1.5} alignItems="center">
                                            <Typography variant="h5" fontWeight="bold" gutterBottom>
                                                IOC Database Results
                                            </Typography>
                                            <Chip
                                                label="per-report extractions"
                                                size="small"
                                                sx={{ height: 20, fontSize: '0.65rem', color: cyber.accent.cyan, bgcolor: alpha(cyber.accent.cyan, 0.1), border: `1px solid ${alpha(cyber.accent.cyan, 0.3)}`, mb: 1 }}
                                            />
                                        </Stack>
                                        <Typography variant="body2" color="text.secondary">
                                            Found <Box component="strong" sx={{ color: cyber.accent.cyan, fontFamily: cyber.fontMono }}>{results.length}</Box> {results.length === 1 ? 'result' : 'results'} for "<strong>{submittedTerm}</strong>" among the indicators extracted from individual reports
                                        </Typography>
                                    </Box>
                                    {results.length > 0 && (
                                        <Chip
                                            label={`${results.length} matches`}
                                            sx={{
                                                fontWeight: 'bold',
                                                fontFamily: cyber.fontMono,
                                                color: cyber.accent.cyan,
                                                bgcolor: alpha(cyber.accent.cyan, 0.12),
                                                border: `1px solid ${alpha(cyber.accent.cyan, 0.4)}`,
                                            }}
                                        />
                                    )}
                                </Stack>
                            </Paper>

                            {results.length > 0 ? (
                                <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                                    <StyledTableContainer>
                                        <Table>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Indicator</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Source Article</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }} align="right">Actions</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {results.map((result) => (
                                                    <TableRow key={result.id}>
                                                        <TableCell>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <FingerprintIcon sx={{ fontSize: 18, color: alpha(cyber.accent.cyan, 0.7) }} />
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{
                                                                        wordBreak: 'break-all',
                                                                        fontFamily: cyber.fontMono,
                                                                        fontSize: '0.8rem',
                                                                        fontWeight: 500,
                                                                        color: alpha(cyber.accent.cyan, 0.9)
                                                                    }}
                                                                >
                                                                    {result.indicator}
                                                                </Typography>
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell>
                                                            <TypeChip 
                                                                label={result.type} 
                                                                size="small" 
                                                                color={getTypeColor(result.type)}
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            {result.article ? (
                                                                <Link 
                                                                    href={result.article.articleUrl} 
                                                                    target="_blank" 
                                                                    rel="noopener noreferrer"
                                                                    sx={{ 
                                                                        display: 'flex', 
                                                                        alignItems: 'center', 
                                                                        gap: 0.5,
                                                                        fontWeight: 500
                                                                    }}
                                                                >
                                                                    <ArticleIcon sx={{ fontSize: 16, color: cyber.accent.green }} />
                                                                    {result.article.title}
                                                                    <OpenInNewIcon sx={{ fontSize: 14 }} />
                                                                </Link>
                                                            ) : (
                                                                <Typography variant="body2" sx={{ color: alpha(cyber.accent.amber, 0.8) }}>
                                                                    Article not found
                                                                </Typography>
                                                            )}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {result.article && (
                                                                <Button 
                                                                    component={RouterLink} 
                                                                    to={`/report/${result.articleId}`}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    startIcon={<VisibilityIcon />}
                                                                    sx={{ 
                                                                        textTransform: 'none',
                                                                        borderRadius: 1
                                                                    }}
                                                                >
                                                                    View Report
                                                                </Button>
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </StyledTableContainer>
                                </Paper>
                            ) : (
                                <Alert 
                                    severity="info" 
                                    sx={{ 
                                        borderRadius: 2,
                                        '& .MuiAlert-message': {
                                            width: '100%'
                                        }
                                    }}
                                >
                                    <Stack spacing={1}>
                                        <Typography variant="body1" fontWeight="bold">
                                            No results found
                                        </Typography>
                                        <Typography variant="body2">
                                            The indicator "<strong>{searchTerm}</strong>" was not found in our database. 
                                            Try searching for a different IOC or check the format.
                                        </Typography>
                                    </Stack>
                                </Alert>
                            )}

                            {/* Companion lookup: same value against the Knowledge Graph */}
                            <KGIndicatorMatches query={submittedTerm} />
                        </Box>
                    </Fade>
                )}
            </Container>
        </Box>
    );
};

export default IOCSearchPage;